<template>
  <div class="generator-review card p-4 mb-3">
    <h3 class="mb-1 mb-sm-4">
      {{ $t("signup.add_generator_component.title") }}
    </h3>
    <TextFieldComponent
      v-model="localGenerator.device_name"
      field="signup.lv.generator_review.device_name"
      :rules="rules.required"
    />
    <SelectFieldComponent
      v-model="localGenerator.device_technology"
      field="signup.lv.generator_review.device_technology"
      :options="technologyOptions"
      :rules="rules.required"
    />
    <NumberFieldComponent
      v-model="localGenerator.device_power"
      field="signup.lv.generator_review.device_power"
      :rules="rules.devicePower"
    />
    <NumberFieldComponent
      v-model="localGenerator.device_power_in_permit"
      field="signup.lv.generator_review.device_power_in_permit"
      :rules="rules.devicePower"
    />
    <TextFieldComponent
      v-model="localGenerator.device_eic"
      field="signup.lv.generator_review.device_eic"
      :rules="rules.required"
    />
    <SelectFieldComponent
      v-model="localGenerator.only_device_output"
      field="signup.lv.generator_review.only_device_output"
      :options="YES_NO_OPTIONS"
      :rules="rules.required"
    />
    <TextFieldComponent
      v-model="localGenerator.device_address"
      field="signup.lv.generator_review.device_address"
      :rules="rules.required"
    />
    <NumberFieldComponent
      v-model="localGenerator.lat"
      field="signup.lv.generator_review.lat"
      :rules="rules.required"
    />
    <NumberFieldComponent
      v-model="localGenerator.lng"
      field="signup.lv.generator_review.lng"
      :rules="rules.required"
    />
    <SelectFieldComponent
      v-model="localGenerator.device_grid_operator"
      field="signup.lv.generator_review.device_grid_operator"
      :options="gridOperatorOptions"
      :rules="rules.required"
    />
    <TextFieldComponent
      v-model="localGenerator.grid_contract_start_date"
      type="date"
      field="signup.lv.generator_review.grid_contract_start_date"
    />
    <div class="row mb-2 g-2">
      <label for="has-subsidy" class="col-sm-12 pb-0 col-form-label">
        {{ $t("signup.add_generator_component.subsidy_question") }}
      </label>
      <div class="col-sm-11">
        <div class="form-check" role="button">
          <input
            class="form-check-input pe-auto"
            role="button"
            type="radio"
            name="subsidy_radio"
            id="has-subsidy-no"
            :value="false"
            v-model="hasSubsidy"
          />
          <label class="form-check-label" for="has-subsidy-no" role="button">
            {{ $t("common.no") }}
          </label>
        </div>
        <div class="form-check" role="button">
          <input
            class="form-check-input"
            role="button"
            type="radio"
            name="subsidy_radio"
            id="has-subsidy-yes"
            :value="true"
            v-model="hasSubsidy"
          />
          <label class="form-check-label" for="has-subsidy-yes" role="button">
            {{ $t("common.yes") }}
          </label>
        </div>
      </div>
    </div>

    <!-- Subsidy Management Section -->
    <div v-if="hasSubsidy" class="row mb-2 gy-3 gx-0 mt-1">
      <label class="col-form-label mb-0 pb-0 mt-0">
        {{ $t("signup.add_generator_component.subsidy_title") }}
      </label>
      <div v-for="(_subsidy, index) in localGenerator.subsidies" :key="index">
        <SubsidyCardComponent
          v-model:subsidy="localGenerator.subsidies[index]"
          :removeSubsidy="() => removeSubsidy(index)"
        />
      </div>
      <button class="btn btn-subtle-primary col-sm-11" @click="addSubsidy">
        <font-awesome-icon icon="plus" />
        {{ $t("signup.add_generator_component.add_subsidy") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import { type FieldOption, YES_NO_OPTIONS } from "@/signup/common/fieldOptions";
import NumberFieldComponent from "@/signup/common/NumberFieldComponent.vue";
import SelectFieldComponent from "@/signup/common/SelectFieldComponent.vue";
import TextFieldComponent from "@/signup/common/TextFieldComponent.vue";
import SubsidyCardComponent from "@/signup/lv/SubsidyCardComponent.vue";
import type { Account, Generator } from "@/types";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  generatorIndex: number;
  companyIndex: number;
  accountData: Account;
}>();

const emit = defineEmits<{
  handleUpdateGenerator: [generator: Generator];
}>();

const { t } = useI18n();
const generator = computed(() => {
  return props.accountData.companies[props.companyIndex].generators[
    props.generatorIndex
  ];
});
const localGenerator = reactive({
  ...generator.value,
});

const { required, integer, min } = useValidators(t);
const rules = {
  required: { required },
  devicePower: { required, integer, min: min(0) },
};
const technologyOptions: FieldOption<string>[] = ["solar", "wind", "hydro"].map(
  (value) => ({
    value,
    label: `signup.lv.generator_review.device_technology.options.device_technology_${value}`,
  })
);
const gridOperatorOptions: FieldOption<"TSO" | "DSO">[] = [
  {
    value: "TSO",
    label:
      "signup.lv.generator_review.device_grid_operator.options.grid_operator_tso",
  },
  {
    value: "DSO",
    label:
      "signup.lv.generator_review.device_grid_operator.options.grid_operator_dso",
  },
];

watch(
  localGenerator,
  (newValue) => {
    emit("handleUpdateGenerator", newValue);
  },
  { deep: true }
);

const hasSubsidy = ref(false);
if (generator.value.subsidies && generator.value.subsidies.length > 0) {
  hasSubsidy.value = true;
}

// Initialize subsidies array if it doesn't exist
if (!localGenerator.subsidies) {
  localGenerator.subsidies = [];
}

const addSubsidy = () => {
  localGenerator.subsidies.push({
    provider: "",
    decision_date: null,
    plan: null,
    amount: null,
  });
};

const removeSubsidy = (index: number) => {
  localGenerator.subsidies.splice(index, 1);
  if (localGenerator.subsidies.length === 0) {
    hasSubsidy.value = false;
  }
};

watch(hasSubsidy, (newValue) => {
  if (newValue) {
    if (localGenerator.subsidies.length === 0) {
      addSubsidy();
    }
  } else {
    localGenerator.subsidies = [];
  }
});
</script>

<style scoped lang="scss">
.generator-review {
  .col-form-label {
    text-align: left;
    font-weight: bold;
    padding: 0;
  }
  .row {
    margin-left: 0;
    margin-right: 0;
  }
}
@media (max-width: 575.98px) {
  .generator-review {
    .col-form-label {
      margin-top: 1rem;
    }
  }
  h3 {
    font-size: 1.2rem;
  }
}
</style>
